import { useRef, useEffect, useState } from "react";
import SimpleBar from "simplebar";
import { useLocation, Link } from "react-router-dom";
import useMenus from "../hooks/useMenus";
import { useNavigate } from "react-router-dom";
import useAdminSettings from "../hooks/useAdminSettings";
import SafeImage from "../components/common/SafeImage";
import logoImgFallback from "../assets/img/logo-img.png";

export default function Sidebar(prop) {
  const scrollRef = useRef(null);
  const menus = useMenus();
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useAdminSettings();
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem("user"));
  const userRole = user?.role || "admin";
  // Compute logo URL
  const rawBackendUrl = import.meta.env.VITE_API_URL || "http://localhost:8000";
  const backendBaseUrl = rawBackendUrl.endsWith("/")
    ? rawBackendUrl
    : `${rawBackendUrl}/`;

  let logoUrl = settings["website.logo"];

  if (logoUrl) {
    // If logo exists in settings, check if it's full URL
    logoUrl = logoUrl.startsWith("http") ? logoUrl : backendBaseUrl + (logoUrl.startsWith("/") ? logoUrl.substring(1) : logoUrl);
  } else {
    // Default image if no logo is set
    logoUrl = logoImgFallback;
  }

  // Logo style
  const LOGO_W = "auto";
  const LOGO_H = 45;
  const logoStyle = {
    width: `${LOGO_W}px`,
    maxHeight: `${LOGO_H}px`,
    objectFit: "contain",
    display: "block",
  };
  // States
  const [activeSidebarIndex, setActiveSidebarIndex] = useState(
    prop.MenuId ?? null,
  );
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState(null);
  const [isCompactWrapper, setIsCompactWrapper] = useState(
    prop.wrapperType === "compact-wrapper",
  );
  const [isHorizontalWrapper, setIsHorizontalWrapper] = useState(
    prop.wrapperType === "horizontal-wrapper",
  );
  const [isCompactSidebar, setIsCompactSidebar] = useState(
    prop.wrapperType === "compact-sidebar",
  );
  // Adjust layout on window resize
  useEffect(() => {
    if (isHorizontalWrapper) {
      const checkPosition = () => {
        if (window.matchMedia("(max-width: 991px)").matches) {
          setIsHorizontalWrapper(false);
          setIsCompactWrapper(true);
        }
      };
      window.addEventListener("resize", checkPosition);
      checkPosition();
      return () => window.removeEventListener("resize", checkPosition);
    }
  }, [isHorizontalWrapper]);
  // Highlight current route
  useEffect(() => {
    if (isCompactSidebar && window.innerWidth > 992) {
      document.body.insertAdjacentHTML(
        "beforeend",
        '<div class="bg-overlay1"></div>',
      );
    }
    document.querySelectorAll(".sidebar-wrapper nav ul>li a").forEach((el) => {
      const href = el.getAttribute("href");
      if (href && location.pathname.includes(href)) {
        el.classList.add("active");
        el.closest("ul")?.previousElementSibling?.classList.add("active");
      }
    });
  }, [location.pathname, isCompactSidebar]);
  // Toggle
  const toggleSidebar = (index) => {
    setActiveSidebarIndex(activeSidebarIndex === index ? null : index);
  };
  const toggleSubmenu = (index) => {
    setActiveSubmenuIndex(activeSubmenuIndex === index ? null : index);
  };
  // SimpleBar init
  useEffect(() => {
    if (scrollRef.current) {
      new SimpleBar(scrollRef.current, { autoHide: true });
    }
  }, []);
  // Logo click
  // const handleLogoClick = (e) => {
  //   e.preventDefault();
  //   if (userRole === "employee") {
  //     navigate("/employee-dashboard");
  //   } else {
  //     navigate("/dashboard");
  //   }
  // };
  return (
    <>
      <div ref={prop.sidebarRef} className="sidebar-wrapper" style={{paddingBottom:"15px"}}>
        <div id="sidebarEffect"></div>
        <div>
          {/* :white_check_mark: Single Logo Block - Left aligned */}
          <div
            className="logo-wrapper logo-wrapper-center"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "12px",
              marginLeft: "10px",
            }}
          >
            <a href="#" title="">
              <SafeImage
                className="img-fluid main-logo"
                src={logoUrl}
                fallbackSrc={logoImgFallback}
                alt="logo"
                style={logoStyle}
              />
            </a>
            <div className="back-btn" onClick={prop.toggleSidebar}>
              <i className="fa fa-angle-left"></i>
            </div>
            <div
              ref={prop.toggleSidebarRef}
              className="toggle-sidebar"
              onClick={prop.toggleSidebar}
            >
              <i className="ri-apps-line status_toggle middle sidebar-toggle"></i>
            </div>
          </div>
          <nav className="sidebar-main">
            <div className="left-arrow" id="left-arrow">
              <i data-feather="arrow-left"></i>
            </div>
            <div>
              <ul ref={scrollRef} className="sidebar-links" id="simple-bar">
                <li className="back-btn"></li>
                {menus.map((menu, index) => (
                  <li key={index} className="sidebar-list">
                    <a
                      className={`linear-icon-link sidebar-link sidebar-title ${activeSidebarIndex === index ? "active" : ""
                        }`}
                      href={menu.url || "#"}
                      onClick={(e) => {
                        if (menu.submenu.length > 0) {
                          e.preventDefault();
                          toggleSidebar(index);
                        }
                      }}
                    >
                      <i className={menu.icon}></i>
                      <span>{menu.menuName}</span>
                      {menu.submenu.length > 0 ? (
                        <i
                          className={`ri-arrow-${activeSidebarIndex === index ? "down" : "right"
                            }-s-line`}
                        ></i>
                      ) : (
                        ""
                      )}
                    </a>
                    <ul
                      style={{
                        display:
                          activeSidebarIndex === index ? "block" : "none",
                      }}
                      className="sidebar-submenu menu-content"
                    >
                      {menu.submenu.map((subMenu, subIndex) => (
                        <li key={subIndex}>
                          <Link
                            className={`${activeSubmenuIndex === subIndex ? "active" : ""
                              }`}
                            to={subMenu.url}
                            onClick={(e) => {
                              e.preventDefault();
                              toggleSubmenu(subIndex);
                              navigate(subMenu.url);
                            }}
                          >
                            <div>{subMenu.menuName}</div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
            <div className="right-arrow" id="right-arrow">
              <i data-feather="arrow-right"></i>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
